import { memo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Layout, PageTitle, CashSpecialIcon } from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { FlowHeroMark } from "../components/FlowHeroMark";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";

import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useCashDepositContext } from "../hooks/useCashDepositContext";
import { APP_ROUTES } from "../constants";
import { amountFormatter } from "../utils";
import { getEvents, safeCapture } from "../analytics";
import { shouldPromptPhone } from "../utils/phoneGate";

const VERIFICATION_DURATION_MS = 2000;

/**
 * Cash-deposit verification screen. Shows a spinner with the amount while the
 * deposit is being verified, then advances to the phone prompt or Done.
 */
export const PaymentVerificationPage = memo(() => {
  const { t } = useTranslation();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { context } = useCashDepositContext();
  const colorConfig = theme.whiteView;
  const { dropzoneColors, bodyCopy, backgroundIcon } = colorConfig;
  const { redirectTo } = useRouteWithToken();

  const isStandalone = session.data?.config?.type === "standalone";
  const amount = isStandalone
    ? session.data?.config?.amount
    : context?.amount?.value;
  const currency = isStandalone
    ? session.data?.config?.sourceCurrency
    : context?.amount?.currency;

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("barcode"), {
      operation_id: context?.operationId,
    });
    // Fire once on mount; operation_id is best-effort at that moment.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const data = useSessionStore.getState().session.data;
      const prompt = shouldPromptPhone(data, data?.linkId);
      redirectTo(
        prompt ? APP_ROUTES.PHONE_PAGE : APP_ROUTES.DEPOSIT_ON_THE_WAY_PAGE,
        {
          replace: true,
          state: { next: APP_ROUTES.DEPOSIT_ON_THE_WAY_PAGE },
        },
      );
    }, VERIFICATION_DURATION_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuraLayout colorConfig={colorConfig} className="flow-landing-page">
      <Layout.Content className="landing-section flow-hero-section">
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        <section className="flow-hero">
          <FlowHeroMark
            showSpinner
            dropzoneColors={dropzoneColors}
            backgroundIcon={backgroundIcon}
          >
            <CashSpecialIcon size={60} colors={dropzoneColors} />
          </FlowHeroMark>
          <section className="landing-container">
            <PageTitle
              highlight={t("cashDeposit.verifying")}
              highlightColor={bodyCopy}
              secudnary={
                amount != null
                  ? `$${amountFormatter(amount)} ${currency ?? "USD"}`
                  : ""
              }
              secudnaryColor={bodyCopy}
            />
          </section>
        </section>
      </Layout.Content>
    </AuraLayout>
  );
});
